import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Translate, { useTranslateText } from "../../Components/Multilanguage/Translate";
import Loading from "../../Components/Loading/Loading";
import { validateGiftSession } from "./service";
import { ReactComponent as CopyIcon } from "../../assets/Icons/copy.svg";
import { ReactComponent as TickIcon } from "../../assets/Icons/tick.svg";

const POLL_INTERVAL = 12000;
const POLL_TIMEOUT = 60000;

const GiftSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [message, setMessage] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const [searchParams] = useSearchParams();
  const session_id = searchParams.get("session_id");
  const navigate = useNavigate();
  const translateText = useTranslateText();

  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };

  useEffect(() => {
    let intervalId;
    let timeoutId;
    let isMounted = true;

    const pollSession = async () => {
      if (!session_id) {
        navigate("/gift/failed", { state: { message } });
        return;
      }
      try {
        const response = await validateGiftSession(appInfo, session_id);
        if (response?.status === 200 && response?.data?.success) {
          const { message, gift_coupon } = response.data;
          if (isMounted) {
            setIsValid(true);
            setMessage(message);
            setGiftCode(gift_coupon || "");
            setLoading(false);
            setChecking(false);
            clearInterval(intervalId);
            clearTimeout(timeoutId);
          }
        } else if (response?.status === 404) {
          if (isMounted) {
            setIsValid(false);
            setMessage(<Translate textKey={'payment_delay_message'} />);
            setGiftCode("");
          }
        } else {
          if (isMounted) {
            setChecking(false);
            setIsValid(false);
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            navigate("/gift/failed", { state: { message: response?.data?.error || <Translate textKey={'error_message'} /> } });
          }
        }
      } catch (error) {
        if (isMounted) {
          setChecking(false);
          setIsValid(false);
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          navigate("/gift/failed", { state: { message: error?.message || <Translate textKey={'error_message'} /> } });
        }
      }
      if (isMounted) setPollCount((c) => c + 1);
    };

    if (session_id) {
      setLoading(false);
      setChecking(true);
      pollSession();
      intervalId = setInterval(pollSession, POLL_INTERVAL);
      timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        setChecking(false);
        setIsValid(false);
      }, POLL_TIMEOUT);
    } else {
      navigate("/gift/failed", { state: { message } });
    }

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [session_id]);

  const handleOkClick = () => {
    navigate("/");
  };

  const handleCopy = () => {
    if (giftCode) {
      const url = `${window.location.origin}/gift/claim?gc=${giftCode}`;
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="giftSuccess">
      <div className="successContainer">
        {!checking && isValid && (
          <div className="successIcon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="92"
              height="92"
              fill="#28a745"
              viewBox="0 0 16 16"
            >
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
            </svg>
          </div>
        )}
        {checking && (
          <p className="checking"><Translate textKey={'payment_checking_inprogress'} /></p>
        )}
        {!checking && <p>{message}</p>}
        {!checking && isValid && (
          <div className="giftCodeContainer">
            <h3>
              <Translate textKey="your_gift_code" />
            </h3>
            <div className="giftCodeDisplay">
              <span>{giftCode}</span>
              <button
                className={`copyGiftCodeBtn${copied ? " copied" : ""}`}
                onClick={handleCopy}
                title={copied ? translateText("copied") : "Copy claim URL"}
                aria-label="Copy claim URL"
                type="button"
              >
                <span className="icon-wrapper">
                  <CopyIcon width={20} height={20} className={copied ? "icon-hide" : "icon-show"} />
                  <TickIcon width={20} height={20} className={copied ? "icon-show" : "icon-hide"} />
                </span>
              </button>
            </div>
          </div>
        )}
        {!checking && (
          <div className="actionButtons">
            <button className="okBtn" onClick={handleOkClick}>
              <Translate textKey="ok" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftSuccess;
